import React, { useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import chunk from "lodash/chunk";
import Swiper from "swiper";
import cx from "classnames";
import "../../../polyfill/ie11CustomProperties";

import { SliderButton } from "../SliderButton";
import { SliderIndicator } from "../SliderIndicator";

import styles from "./TileSlider.scss";

const swiperBaseConfig = {
  centeredSlides: true,
  centeredSlidesBounds: true,
  slideToClickedSlide: true,
  slidesPerView: "auto",
};

const variantConfig = {
  narrow: {
    ...swiperBaseConfig,
    group: 1,
    breakpoints: {
      640: {
        group: 2,
      },
      1024: {
        group: 3,
      },
      1344: {
        group: 4,
      },
    },
  },
  wide: {
    ...swiperBaseConfig,
    group: 1,
    breakpoints: {
      640: {
        group: 1,
      },
      1024: {
        group: 2,
      },
      1344: {
        group: 3,
      },
    },
  },
};

const Slider = ({ children, variant }) => {
  const swiperNode = useRef();
  const [swiper, setSwiper] = useState(null);
  const [breakpoint, setBreakpoint] = useState(null);
  const [active, setActive] = useState(0);
  const [focusIndex, setFocusIndex] = useState(0);
  const [itemRefs, setItemRefs] = useState([]);
  // const [reachBeginning, setReachBeginning] = useState(true);
  // const [reachEnd, setReachEnd] = useState(false);
  const config = variantConfig[variant];
  const itemsFromChildren = (group) => {
    const asArray = React.Children.toArray(children);
    return chunk(asArray, group);
  };
  const [, { group: initialGroup }] = Object.entries(config.breakpoints)
    .reverse()
    .find(
      ([bp]) =>
        ((typeof document !== "undefined" &&
          document.body &&
          document.body.clientWidth) ||
          320) >= bp
    ) || ["default", config];
  const [items, setItems] = useState(itemsFromChildren(initialGroup));

  const reachBeginning = useMemo(() => {
    if (!swiper) {
      return true;
    }
    return active === 0;
  }, [active]);

  const reachEnd = useMemo(() => {
    if (!swiper) {
      return false;
    }
    return active + 1 === swiper.slides.length;
  }, [active]);

  const assignBreakpoint = (value) => {
    setBreakpoint(value === "max" ? 0 : value);
  };

  const buildSwiper = () => {
    const instance = new Swiper(swiperNode.current, config);

    const { currentBreakpoint } = instance;
    assignBreakpoint(currentBreakpoint);

    // Workaround for swiper bug
    // @see https://github.com/nolimits4web/swiper/issues/3108#issuecomment-635233795
    instance.on("transitionEnd", () => {
      setActive(instance.snapIndex);
    });

    instance.on("resize", () => {
      if (breakpoint !== instance.currentBreakpoint) {
        assignBreakpoint(instance.currentBreakpoint);
      }
    });

    setSwiper(instance);
  };

  const destroySwiper = () => {
    if (swiper && swiperNode.current !== null) {
      swiper.destroy(true, true);
      setSwiper(null);
    }
  };

  const rebuildSwiper = () => {
    destroySwiper();
    if (swiper) {
      swiper.init();
    }
  };

  const getGroupSize = () => {
    if (breakpoint === 0) {
      return variantConfig[variant].group;
    }
    return variantConfig[variant].breakpoints[breakpoint].group;
  };

  const goNext = () => {
    if (!reachEnd) {
      swiper.slideNext();

      const groupSize = getGroupSize();
      const indexFirstItemNextGroup = groupSize * (active + 1);

      if (groupSize === 1) {
        itemRefs[active + 1].current.focus();
        setFocusIndex(active + 1);
      } else {
        itemRefs[indexFirstItemNextGroup].current.focus();
        setFocusIndex(indexFirstItemNextGroup);
      }
    }
  };

  const goPrev = () => {
    if (!reachBeginning) {
      swiper.slidePrev();

      const groupSize = getGroupSize();
      const indexLastItemPrevGroup = groupSize * active - 1;

      if (groupSize === 1) {
        itemRefs[active - 1].current.focus();
        setFocusIndex(active - 1);
      } else {
        itemRefs[indexLastItemPrevGroup].current.focus();
        setFocusIndex(indexLastItemPrevGroup);
      }
    }
  };

  const slideTo = (index) => {
    swiper.slideTo(index);
  };

  // Build swiper on mount, register destroy method on unmount
  useEffect(() => {
    if (!swiper && swiperNode.current) {
      buildSwiper();
    }

    return () => {
      destroySwiper();
    };
  }, []);

  // Rebuild swiper when specific props change
  useEffect(() => {
    rebuildSwiper();
  }, [variant]);

  // Update items on children or config change
  useEffect(() => {
    if (swiper) {
      setItems(itemsFromChildren(swiper.params.group));
      const refs = [];
      for (let i = 0; i < React.Children.count(children); i += 1) {
        refs.push(React.createRef());
      }
      setItemRefs(refs);
    }
  }, [children, breakpoint]);

  // Update swiper when items change
  useEffect(() => {
    if (swiper) {
      swiper.update();
    }
  }, [items]);

  const getGroupKey = (group) => {
    const key = group.map((g) => g.key).join("-");
    return key;
  };

  function handleKeyboardControls(event) {
    switch (event.key) {
      case "ArrowRight":
        goNext();
        break;
      case "ArrowLeft":
        goPrev();
        break;
      default:
    }

    // tabbing / shift-tabbing through elements will trigger page change once bounds are reached
    const isOutOfLeftGroupBounds = focusIndex % getGroupSize() === 0;
    const isOutOfRightGroupBounds = (focusIndex + 1) % getGroupSize() === 0;

    if (event.key === "Tab") {
      if (event.shiftKey) {
        if (focusIndex !== 0) {
          event.preventDefault();
          if (isOutOfLeftGroupBounds) {
            goPrev();
          } else {
            itemRefs[focusIndex - 1].current.focus();
            setFocusIndex(focusIndex - 1);
          }
        }
      } else if (focusIndex < React.Children.count(children) - 1) {
        event.preventDefault();
        if (isOutOfRightGroupBounds) {
          goNext();
        } else {
          itemRefs[focusIndex + 1].current.focus();
          setFocusIndex(focusIndex + 1);
        }
      }
    }
  }

  return (
    <div className={cx(styles.slider)}>
      <div className={styles[variant]}>
        <div className={styles.sliderButton}>
          <SliderButton
            onClick={goPrev}
            disabled={reachBeginning}
            variant="backwards"
            onKeyDown={handleKeyboardControls}
          />
        </div>
        <div className={styles.body}>
          <div
            ref={swiperNode}
            className={cx(styles.swiperContainer, "swiper-container")}
          >
            <div className={cx(styles.swiperWrapper, "swiper-wrapper")}>
              {items.map((group, groupIndex) => (
                <SliderItem key={getGroupKey(group)}>
                  {group.map((child, itemIndex) => (
                    <SliderContent
                      ref={(el) => {
                        const i = items[0].length * groupIndex + itemIndex;

                        if (itemRefs[i]) {
                          itemRefs[i].current = el;
                        }
                      }}
                      tabIndex={groupIndex === active ? "0" : "-1"}
                      key={child.key}
                      onKeyDown={handleKeyboardControls}
                    >
                      {child}
                    </SliderContent>
                  ))}
                </SliderItem>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.sliderButton}>
          <SliderButton
            onClick={goNext}
            disabled={reachEnd}
            variant="forwards"
            onKeyDown={handleKeyboardControls}
          />
        </div>
      </div>
      <div className={styles.indicatorsWrapper}>
        <div className={styles.indicators}>
          <SliderIndicator
            activeIndex={active}
            amount={items.length}
            onActivate={(index) => slideTo(index)}
          />
        </div>
      </div>
    </div>
  );
};

Slider.displayName = "TileSlider";

Slider.defaultProps = {
  variant: "narrow",
};

Slider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  variant: PropTypes.oneOf(["narrow", "wide"]),
};

const SliderItem = ({ children }) => (
  <div className={cx(styles.swiperSlide, styles.slide, "swiper-slide")}>
    {children}
  </div>
);

SliderItem.propTypes = {
  children: PropTypes.node.isRequired,
};

SliderItem.displayName = "TileSlider.Item";

const SliderContent = React.forwardRef(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.content} {...props}>
    {children}
  </div>
));

SliderContent.displayName = "TileSlider.Content";

SliderContent.propTypes = {
  children: PropTypes.node.isRequired,
};

export const TileSlider = Slider;

export default Slider;
